import { AxiosError } from 'axios';

import api from './api';
import { clearCsrfToken, setCsrfToken } from './sessionManager';

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    return (
      axiosError.response?.data?.message ||
      axiosError.response?.data?.error ||
      'Ocorreu um erro na requisicao. Tente novamente.'
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ocorreu um erro na requisicao. Tente novamente.';
};

const rethrowPreservingAxiosError = (error: unknown): never => {
  if (error instanceof AxiosError) {
    throw error;
  }

  throw new Error(getErrorMessage(error));
};

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  subscriptionStatus: 'trialing' | 'active' | 'canceled' | 'none';
  plan: 'trial' | 'familia' | 'clinica' | 'none';
  trialEndsAt: string | null;
}

export interface LoginResponseUser {
  id: string;
  username: string;
  email: string;
}

interface SessionResponseUser {
  id: string;
  email: string;
  name: string;
  subscriptionStatus: 'trialing' | 'active' | 'canceled' | 'none';
  plan: 'trial' | 'familia' | 'clinica' | 'none';
  trialEndsAt: string | null;
}

export interface LoginResponse {
  accessToken?: string;
  csrfToken?: string;
  user: LoginResponseUser;
}

export interface RefreshSessionResponse {
  accessToken?: string;
  csrfToken?: string;
  user?: LoginResponseUser;
}

export const toAuthUser = (
  user: LoginResponseUser | SessionResponseUser,
): AuthUser => {
  if ('name' in user) {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      subscriptionStatus: user.subscriptionStatus ?? 'none',
      plan: user.plan ?? 'none',
      trialEndsAt: user.trialEndsAt ?? null,
    };
  }

  return {
    id: user.id,
    email: user.email,
    name: user.username,
    subscriptionStatus: 'none',
    plan: 'none',
    trialEndsAt: null,
  };
};

const isValidLoginResponse = (data: LoginResponse): boolean => {
  return Boolean(
    data.user &&
    data.user.id &&
    data.user.username &&
    data.user.email,
  );
};

export const login = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  try {
    const { data } = await api.post<LoginResponse>('/api/auth/login', {
      email,
      password,
    });

    if (!isValidLoginResponse(data)) {
      throw new Error('Resposta da API nao contem os dados esperados.');
    }

    setCsrfToken(data.csrfToken);

    return data;
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};

export const loginWithGoogle = async (
  idToken: string,
  nonce?: string,
): Promise<LoginResponse> => {
  try {
    const { data } = await api.post<LoginResponse>('/api/auth/google', {
      idToken,
      ...(nonce ? { nonce } : {}),
    });

    if (!isValidLoginResponse(data)) {
      throw new Error('Resposta da API nao contem os dados esperados.');
    }

    setCsrfToken(data.csrfToken);

    return data;
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};

export const register = async (
  username: string,
  email: string,
  password: string,
): Promise<{ message: string }> => {
  try {
    const { data } = await api.post<{ message: string }>('/api/auth/register', {
      username,
      email,
      password,
    });

    if (!data.message) {
      throw new Error('Resposta da API nao contem a mensagem esperada.');
    }

    return data;
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};

export const getCurrentUser = async (): Promise<AuthUser> => {
  try {
    const { data } = await api.get<SessionResponseUser>('/api/auth/me');

    if (!data.id || !data.email || !data.name) {
      throw new Error('Resposta da API nao contem os dados esperados.');
    }

    return {
      id: data.id,
      email: data.email,
      name: data.name,
      subscriptionStatus: data.subscriptionStatus ?? 'none',
      plan: data.plan ?? 'none',
      trialEndsAt: data.trialEndsAt ?? null,
    };
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};

export const refreshSession = async (): Promise<RefreshSessionResponse> => {
  try {
    const { data } = await api.post<RefreshSessionResponse>('/api/auth/refresh', {});
    setCsrfToken(data?.csrfToken);
    return data;
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/api/auth/logout', {});
    clearCsrfToken();
  } catch (error) {
    rethrowPreservingAxiosError(error);
  }
};
